import React from "react";
import { Typography, useTheme } from "@mui/material";

/**
 * Componente de texto personalizado que utiliza Material-UI `Typography` para mostrar texto con varias opciones de estilo.
 * @param {Object} props - Propiedades del componente.
 * @param {React.ElementType} [props.component="p"] - El tipo de componente HTML que se utilizará para renderizar el texto (por ejemplo, "span", "div", "p").
 * @param {string} [props.variant="body1"] - Variante tipográfica de Material-UI. Puede ser una de las variantes definidas en Material-UI (por ejemplo, "h1", "h2", "body1").
 * @param {React.ReactNode} props.children - El contenido del texto a renderizar (puede ser texto o elementos React).
 * @param {string} [props.align="center"] - Alineación del texto (por ejemplo, "left", "center", "right").
 * @param {string} [props.textAlign="center"] - Alineación del texto a nivel CSS.
 * @param {string} [props.textDecoration="none"] - Estilo de decoración del texto (por ejemplo, "underline", "line-through").
 * @param {string} [props.textDecorationColor] - Color de la decoración del texto.
 * @param {string} [props.textTransform="none"] - Transformación del texto (por ejemplo, "uppercase", "lowercase").
 * @param {string} [props.justifyContent="center"] - Alineación del contenido en el contenedor flex (por ejemplo, "center", "flex-start").
 * @param {number|string} [props.marginLeft=0] - Margen izquierdo del texto.
 * @param {string} [props.color="text.primary"] - Color del texto. Si no se especifica, se utiliza el color primario del tema.
 * @param {number|string} [props.marginTop] - Margen superior del texto.
 * @param {Function} [props.onClick] - Función que se ejecuta cuando se hace clic en el texto.
 * @param {string} [props.fontFamily="Lexend"] - Fuente del texto.
 * @param {Object} [props.sx={}] - Estilos adicionales a aplicar, que pueden sobrescribir los valores predeterminados.
 * @returns {React.Element} El componente `Typography` de Material-UI con los estilos y comportamiento personalizados.
 */
const CustomText = ({
  component,
  variant,
  children,
  align,
  textAlign,
  textDecoration,
  textDecorationColor,
  textTransform,
  justifyContent,
  marginLeft,
  color,
  marginTop,
  marginBottom,
  onClick,
  fontFamily,
  fontWeight,
  fontSize,
  width,
  height,
  lineHeight,
  letterSpacing,
  display,
  alignItems,
  gap,
  sx,
}) => {
  /**
   * Contexto del tema actual de Material-UI. Utiliza `useTheme` para obtener el tema configurado en la aplicación.
   * @constant
   * @type {Object}
   */
  const theme = useTheme();
  return (
    <Typography
      onClick={onClick}
      align={align}
      sx={{
        ...{
          textAlign: textAlign ? textAlign : "center",
          textDecoration: textDecoration ? textDecoration : "none",
          textDecorationColor: textDecorationColor ?? undefined,
          textTransform: textTransform ? textTransform : "none",
          fontFamily: fontFamily ? fontFamily : "Lexend",
          justifyContent: justifyContent ? justifyContent : "center",
          marginLeft: marginLeft ? marginLeft : 0,
          color: color ? color : theme.palette.mode,
          marginTop: marginTop ? marginTop : 0,
          marginBottom: marginBottom ? marginBottom : 0,
          fontWeight: fontWeight ? fontWeight : 100,
          fontSize: fontSize
            ? fontSize
            : {
                xs: 15, // Móviles
                sm: 18, // Tabletas en orientación vertical
                md: 19, // Tabletas en orientación horizontal
                lg: 16, // Escritorios
                xl: 17, // Extra grandes
              },
          width: width ?? undefined,
          height: height ?? undefined,
          lineHeight: lineHeight ?? undefined,
          letterSpacing: letterSpacing ?? undefined,
          display: display ?? undefined,
          alignItems: alignItems ?? undefined,
          gap: gap ?? undefined,
        },
        ...sx, // Sobreescribe las propiedades que vengan en sx
      }}
      component={component}
      variant={variant}
    >
      {children}
    </Typography>
  );
};

export default CustomText;
